import { useEffect, useState } from 'react';
import { getDeviceNamesByTables } from '../services/sensor.service';
import { CSVLink } from 'react-csv';
import {
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/es';
import DataTable from '../components/DataTable';
import TableFilters from '../components/TableFilters';
import { useTableDataSearch } from '../hooks/useSearchDataWithFilters';
import { EQUIPOS } from '../hooks/useDBTables';

dayjs.extend(utc);

export default function TableView() {
  const { search } = useTableDataSearch();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  //Filters
  const [startDate, setStartDate] = useState(dayjs().subtract(6, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [dataType, setDataType] = useState('Temperaturas');
  const [smoothingType, setSmoothingType] = useState('Sin suavizar');
  const [prefix, setPrefix] = useState(EQUIPOS[0]);
  const [selectedSensor, setSelectedSensor] = useState('Todos');
  const [deviceOptions, setDeviceOptions] = useState([]);

  const [info, setInfo] = useState({
    type: 'info',
    message: 'Click en BUSCAR para traer datos',
    show: true,
  });

  const handleDataTypeChange = (event) => {
    setDataType(event.target.value);
    setSmoothingType('Sin suavizar');

    if (event.target.value !== 'Temperaturas') {
      setSelectedSensor('Todos');
    }
  };

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
  };

  const handlePrefixChange = (event) => {
    setPrefix(event.target.value);
    setSelectedSensor('Todos');
  };

  const handleSensorChange = (event) => {
    const value = event.target.value;
    setSelectedSensor(value);

    if (value !== 'Todos') {
      const [table] = value.split('-');
      setPrefix(table.trim());
    }
  };

  useEffect(() => {
    getDeviceNamesOptions();
  }, []);

  const getDeviceNamesOptions = async () => {
    const data = await getDeviceNamesByTables(EQUIPOS);
    setDeviceOptions(data);
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const result = await search({
        equipos: EQUIPOS,
        startDate,
        endDate,
        smoothingType,
        dataType,
        prefix,
        selectedSensor,
      });

      if (!result || result.length === 0) {
        setInfo({
          type: 'warning',
          message: 'Sin datos disponibles',
          show: true,
        });
      } else {
        setInfo((prev) => ({ ...prev, show: false }));
      }
      const headerMap = {
        DeviceName: 'Equipo',
        SensorName: 'Nombre',
        SensorValue: 'Valor',
        RecordTime: 'Hora',
        RecordType: 'Tipo',
        Fault: 'Error',
      };

      const filteredResult = result.map(
        ({ idHistoryData, SensorNumber, ...rest }) => {
          const mapped = {};
          for (const key in rest) {
            const newKey = headerMap[key] ?? key;
            mapped[newKey] = rest[key];
          }
          return mapped;
        }
      );
      setData(filteredResult);
    } catch (err) {
      setInfo({
        type: 'error',
        message: err.response.data.error || err.message,
        show: true,
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const headers = data.length
    ? Object.keys(data[0]).map((key) => ({ label: key, key }))
    : [];

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '80vh',
        padding: '2rem',
         mt: 6
      }}
    >
      <head>
        <title>Tabla de registros históricos</title>
      </head>

      <Typography variant="h4" align="center" gutterBottom>
        Tabla de registros históricos
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <TableFilters
          loading={loading}
          startDate={startDate}
          endDate={endDate}
          handleStartDateChange={handleStartDateChange}
          handleEndDateChange={handleEndDateChange}
          dataType={dataType}
          handleDataTypeChange={handleDataTypeChange}
          prefix={prefix}
          handlePrefixChange={handlePrefixChange}
          deviceOptions={deviceOptions}
          smoothingType={smoothingType}
          setSmoothingType={setSmoothingType}
          handleSearch={handleSearch}
          selectedSensor={selectedSensor}
          handleSensorChange={handleSensorChange}
          csvButton={
            <CSVLink
              data={data}
              headers={headers}
              filename="registros_historicos.csv"
            >
              <Button
                variant="contained"
                color="success"
                disabled={data.length === 0}
              >
                📁 Descargar CSV
              </Button>
            </CSVLink>
          }
        />
      </LocalizationProvider>

      {info.show && (
        <Alert
          severity={info.type}
          sx={{ marginBottom: 2 }}
          onClose={() => setInfo((prev) => ({ ...prev, show: false }))}
        >
          {info.message}
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
          >
            <CircularProgress />
          </Box>
        ) : (
          data.length > 0 && (
            <DataTable
              data={data}
              order={order}
              orderBy={orderBy}
              handleRequestSort={handleRequestSort}
            />
          )
        )}
      </Box>
    </Box>
  );
}
